import * as zod from 'zod';

import { FrameOfReference } from '@sb/motion-planning';

import { VelocityExpressedIn } from './TrajectoryControllerTypes';

/**
 * Batch jog-direction feasibility request for the jog panel's button
 * greyout, discriminated on the planner that answers. That must be the
 * planner that would execute the jog, or the greyout can disagree with the
 * motion. Each variant carries only what its planner reads, so a `'tc'`
 * request without `velocityExpressedIn`, or a `'ros'` request without a
 * distance, cannot be written.
 *
 * - `'ros'`: the ROS2 `get_tool_directions` service, the executing planner
 *   for incremental jogs (and continuous jogs while the online-planner flags
 *   are off). Checks reachability over `translationDistance` /
 *   `rotationAngle` in `frameOfReference`, including self-collision.
 *   `rejectWristSingularityCrossing` (incremental only) also rejects steps
 *   that need a wrist flip at the wrist singularity.
 * - `'tc'`: the online planner's jog-directions query, the executing planner
 *   for continuous jogs when `useTrajectoryController &&
 *   onlinePlannerJogging`. Velocity-based and instantaneous-at-pose, so it
 *   takes no distance; `velocityExpressedIn` is the same field `speedLJog`
 *   carries, so feasibility is evaluated in exactly the frames the jog would
 *   use. Includes self-collision: a direction the collision CBF would brake
 *   to a stop reports infeasible, evaluated at the pose the jog would start
 *   from (see formal/jog-directions-requirements.md in arm-control-bot for
 *   the at-rest and shadow-mode divergences).
 */
export const GetToolDirectionsRequest = zod.discriminatedUnion('planner', [
  zod.object({
    planner: zod.literal('ros'),
    translationDistance: zod.number(),
    rotationAngle: zod.number(),
    frameOfReference: FrameOfReference,
    rejectWristSingularityCrossing: zod.boolean(),
  }),
  zod.object({
    planner: zod.literal('tc'),
    velocityExpressedIn: VelocityExpressedIn,
  }),
]);

export type GetToolDirectionsRequest = zod.infer<
  typeof GetToolDirectionsRequest
>;
